import React, { useState, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { AxiosError } from 'axios';
import { ArrowLeft, Search, Menu, CheckCircle, Plus } from 'lucide-react';
import toast from 'react-hot-toast';

import { integrationsApi } from '../../services/integrations-api';
import { quickbooksApi } from '../../services/quickbooks-api';
import { quickbooksLiveApi } from '../../services/quickbooks-live-api';
import { xeroApi } from '../../services/xero-api';
import { lightspeedApi } from '../../services/lightspeed-api';
import { googleDriveApi } from '../../services/google-drive-api';
import { AVAILABLE_INTEGRATIONS, INTEGRATION_CATEGORIES } from '../../config/integrations';
import { useChatLayout } from '../../components/layout/ChatLayout';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import IntegrationCard from '../../components/ui/IntegrationCard';
import IntegrationSetupModal from '../../components/ui/IntegrationSetupModal';
import ConnectTargetOrgSelect from '../../components/integrations/ConnectTargetOrgSelect';
import Button from '../../components/ui/Button';
import Modal, { ModalHeader, ModalBody, ModalFooter } from '../../components/ui/Modal';
import { useConnectTargetOrg } from '../../hooks/useConnectTargetOrg';
import type { BusinessIntegration } from '../../types';

type CategoryFilter = 'all' | 'financial' | 'pos' | 'ecommerce' | 'business' | 'accounting' | 'erp' | 'document';

/** Shape of OAuth-initiation error bodies returned by `/api/integrations/{provider}/connect`. */
interface ConnectErrorData {
  error?: string;
  configurationRequired?: boolean;
}

/** Pull a typed `{ error, configurationRequired }` body out of an unknown axios reject. */
function getConnectErrorData(error: unknown): ConnectErrorData | undefined {
  if (error instanceof AxiosError) {
    return error.response?.data as ConnectErrorData | undefined;
  }
  return undefined;
}

const IntegrationsPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('all');
  const [selectedIntegration, setSelectedIntegration] = useState<BusinessIntegration | null>(null);
  const [isSetupModalOpen, setIsSetupModalOpen] = useState(false);
  const [detailIntegration, setDetailIntegration] = useState<BusinessIntegration | null>(null);

  const navigate = useNavigate();
  const { isMobile, toggleSidebar } = useChatLayout();
  const queryClient = useQueryClient();
  const { orgs, targetOrgId, setTargetOrgId, targetOrgName, hasChoice } = useConnectTargetOrg();

  const { data: connectedIntegrations = [], isLoading: connectionsLoading } = useQuery({
    queryKey: ['connectedIntegrations'],
    queryFn: async () => {
      try {
        return await integrationsApi.getConnectedIntegrations();
      } catch (error) {
        if (error instanceof AxiosError && error.response?.status === 404) {
          return [];
        }
        throw error;
      }
    },
    retry: false,
  });

  const enrichedIntegrations = useMemo(() => {
    return AVAILABLE_INTEGRATIONS.map(integration => {
      const connections = connectedIntegrations.filter(conn => conn.type === integration.type);
      return {
        ...integration,
        isConnected: connections.length > 0,
        connectionCount: connections.length,
      };
    });
  }, [connectedIntegrations]);

  const filteredIntegrations = useMemo(() => {
    return enrichedIntegrations.filter(integration => {
      if (integration.status === 'deprecated') return false;
      const matchesSearch = integration.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           integration.description.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = categoryFilter === 'all' || integration.category === categoryFilter;
      return matchesSearch && matchesCategory;
    });
  }, [enrichedIntegrations, searchQuery, categoryFilter]);

  const availableIntegrations = useMemo(
    () => filteredIntegrations.filter(i => i.status === 'available' || i.status === 'beta'),
    [filteredIntegrations]
  );

  const comingSoonIntegrations = useMemo(
    () => filteredIntegrations.filter(i => i.status === 'coming_soon'),
    [filteredIntegrations]
  );

  const handleConnectIntegration = async (integration: BusinessIntegration): Promise<void> => {
    // The destination business the source will be filed under — explicit, never
    // ambient (R9). The backend re-validates it against the active workspace.
    if (!targetOrgId) {
      toast.error('Choose which business this source belongs to first.');
      return;
    }
    const connectOrgId = targetOrgId;

    if (integration.type === 'quickbooks') {
      // Unreachable while the synced QBO entry is `deprecated` (the backend
      // refuses /connect too). Kept for when the ingestion path is revived.
      try {
        const loadingToast = toast.loading('Redirecting to QuickBooks...');
        const { authorizationUrl } = await quickbooksApi.initiateConnection(connectOrgId);
        toast.dismiss(loadingToast);
        window.location.href = authorizationUrl;
      } catch (error) {
        const errorData = getConnectErrorData(error);
        if (errorData?.configurationRequired) {
          toast.error(
            'QuickBooks integration requires setup. Please configure your QuickBooks app credentials in the backend environment.',
            { duration: 6000 }
          );
        } else {
          toast.error(errorData?.error || 'Failed to connect to QuickBooks');
        }
      }
    } else if (integration.type === 'quickbooks-online-live') {
      // Same Intuit OAuth app as synced — backend routes by `mode` in the state.
      try {
        const loadingToast = toast.loading('Redirecting to QuickBooks...');
        const { authorizationUrl } = await quickbooksLiveApi.initiateConnection(connectOrgId);
        toast.dismiss(loadingToast);
        window.location.href = authorizationUrl;
      } catch (error) {
        const errorData = getConnectErrorData(error);
        if (errorData?.configurationRequired) {
          toast.error(
            'QuickBooks integration requires setup. Please configure your QuickBooks app credentials in the backend environment.',
            { duration: 6000 }
          );
        } else {
          toast.error(errorData?.error || 'Failed to connect to QuickBooks Live');
        }
      }
    } else if (integration.type === 'lightspeed-k') {
      // Unlike the others, the callback lands on a picker rather than a success
      // page: one authorization can span many locations and each becomes its
      // own source.
      try {
        const loadingToast = toast.loading('Redirecting to Lightspeed...');
        const { authorizationUrl } = await lightspeedApi.initiateConnection(connectOrgId);
        toast.dismiss(loadingToast);
        window.location.href = authorizationUrl;
      } catch (error) {
        const errorData = getConnectErrorData(error);
        if (errorData?.configurationRequired) {
          toast.error(
            'Lightspeed integration requires setup. Please configure the Lightspeed API client credentials in the backend environment.',
            { duration: 6000 }
          );
        } else {
          toast.error(errorData?.error || 'Failed to connect to Lightspeed');
        }
      }
    } else if (integration.type === 'xero') {
      try {
        const loadingToast = toast.loading('Redirecting to Xero...');
        const { authorizationUrl } = await xeroApi.initiateConnection(connectOrgId);
        toast.dismiss(loadingToast);
        window.location.href = authorizationUrl;
      } catch (error) {
        const errorData = getConnectErrorData(error);
        if (errorData?.configurationRequired) {
          toast.error(
            'Xero integration requires setup. Please configure your Xero app credentials in the backend environment.',
            { duration: 6000 }
          );
        } else {
          toast.error(errorData?.error || 'Failed to connect to Xero');
        }
      }
    } else if (integration.type === 'google_drive') {
      try {
        const loadingToast = toast.loading('Redirecting to Google...');
        const { authorizationUrl } = await googleDriveApi.initiateConnection(connectOrgId);
        toast.dismiss(loadingToast);
        window.location.href = authorizationUrl;
      } catch (error) {
        const errorData = getConnectErrorData(error);
        if (errorData?.configurationRequired) {
          toast.error(
            'Google Drive integration requires setup. Please configure your Google OAuth credentials in the backend environment.',
            { duration: 6000 }
          );
        } else {
          toast.error(errorData?.error || 'Failed to connect to Google Drive');
        }
      }
    } else {
      setSelectedIntegration(integration);
      setIsSetupModalOpen(true);
    }
  };

  const handleSetupSuccess = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['connectedIntegrations'] });
    void queryClient.invalidateQueries({ queryKey: ['sourcesOverview'] });
    setIsSetupModalOpen(false);
    setSelectedIntegration(null);
  };

  const handleDetailConnect = (): void => {
    if (detailIntegration) {
      setDetailIntegration(null);
      handleConnectIntegration(detailIntegration);
    }
  };

  if (connectionsLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen px-4 py-8 sm:py-12 relative">
      {isMobile && (
        <button
          onClick={toggleSidebar}
          className="absolute top-4 left-4 p-2 rounded-lg text-muted-foreground hover:bg-accent transition-colors"
          aria-label="Open sidebar"
        >
          <Menu className="w-5 h-5" />
        </button>
      )}

      <div className="w-full max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <button
            onClick={() => navigate('/sources')}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Accounts
          </button>
          <h1 className="text-2xl font-bold text-foreground">
            Integrations
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Connect your business tools
          </p>
        </div>

        {/* Destination business — only meaningful with more than one */}
        {hasChoice && (
          <div className="flex flex-wrap items-center justify-between gap-3 p-3 rounded-lg bg-muted border border-border">
            <ConnectTargetOrgSelect
              orgs={orgs}
              value={targetOrgId}
              onChange={setTargetOrgId}
            />
            <p className="text-xs text-muted-foreground">
              New sources are filed under this business.
            </p>
          </div>
        )}

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search integrations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 rounded-lg text-sm bg-muted border border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring/40 focus:border-primary/40 transition-colors"
          />
        </div>

        {/* Category Filter Pills */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setCategoryFilter('all')}
            className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
              categoryFilter === 'all'
                ? 'bg-primary border-primary text-white'
                : 'border-border text-muted-foreground hover:border-primary hover:text-primary'
            }`}
          >
            All
          </button>
          {Object.entries(INTEGRATION_CATEGORIES).map(([key, category]) => (
            <button
              key={key}
              onClick={() => setCategoryFilter(key as CategoryFilter)}
              className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
                categoryFilter === key
                  ? 'bg-primary border-primary text-white'
                  : 'border-border text-muted-foreground hover:border-primary hover:text-primary'
              }`}
            >
              {category.icon} {category.name}
            </button>
          ))}
        </div>

        {/* Available Section */}
        {availableIntegrations.length > 0 && (
          <div>
            <div className="flex items-center gap-3 mb-4">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Available
              </h2>
              <span className="text-[11px] px-2 py-0.5 rounded-full bg-muted border border-border text-muted-foreground">
                {availableIntegrations.length}
              </span>
              <div className="flex-1 h-px bg-muted" />
            </div>
            <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4">
              {availableIntegrations.map((integration) => (
                <IntegrationCard
                  key={integration.id}
                  integration={integration}
                  onConnect={() => setDetailIntegration(integration)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Coming Soon Section */}
        {comingSoonIntegrations.length > 0 && (
          <div>
            <div className="flex items-center gap-3 mb-4">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Coming Soon
              </h2>
              <span className="text-[11px] px-2 py-0.5 rounded-full bg-muted border border-border text-muted-foreground">
                {comingSoonIntegrations.length}
              </span>
              <div className="flex-1 h-px bg-muted" />
            </div>
            <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4">
              {comingSoonIntegrations.map((integration) => (
                <IntegrationCard
                  key={integration.id}
                  integration={integration}
                />
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {filteredIntegrations.length === 0 && (
          <div className="text-center py-12">
            <Search className="w-10 h-10 text-muted-foreground/60 mx-auto mb-3" />
            <h3 className="text-base font-medium text-foreground mb-1">
              No integrations found
            </h3>
            <p className="text-sm text-muted-foreground">
              Try adjusting your search or filter
            </p>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      <Modal
        isOpen={!!detailIntegration}
        onClose={() => setDetailIntegration(null)}
        size="sm"
      >
        {detailIntegration && (
          <>
            <ModalHeader onClose={() => setDetailIntegration(null)}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-muted/50 border border-border flex items-center justify-center overflow-hidden flex-shrink-0">
                  <img
                    src={detailIntegration.logo}
                    alt={detailIntegration.name}
                    className="w-6 h-6 object-contain"
                  />
                </div>
                {detailIntegration.name}
              </div>
            </ModalHeader>
            <ModalBody>
              <p className="text-sm text-muted-foreground leading-relaxed mb-5">
                {detailIntegration.description}
              </p>
              {detailIntegration.features.length > 0 && (
                <ul className="space-y-2">
                  {detailIntegration.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <CheckCircle className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              )}
              {/* Last chance to pick the business — OAuth providers redirect
                  offsite straight from this modal's Connect button. */}
              {detailIntegration.status === 'available' && hasChoice && (
                <div className="mt-5 pt-5 border-t border-border">
                  <ConnectTargetOrgSelect
                    orgs={orgs}
                    value={targetOrgId}
                    onChange={setTargetOrgId}
                  />
                </div>
              )}
            </ModalBody>
            <ModalFooter>
              <Button variant="outline" onClick={() => setDetailIntegration(null)}>
                Close
              </Button>
              {detailIntegration.status === 'available' && (
                <Button
                  onClick={handleDetailConnect}
                  leftIcon={<Plus className="w-4 h-4" />}
                >
                  Connect
                </Button>
              )}
            </ModalFooter>
          </>
        )}
      </Modal>

      {/* Integration Setup Modal */}
      {selectedIntegration && (
        <IntegrationSetupModal
          isOpen={isSetupModalOpen}
          onClose={() => {
            setIsSetupModalOpen(false);
            setSelectedIntegration(null);
          }}
          integration={selectedIntegration}
          organizationId={targetOrgId}
          organizationName={targetOrgName}
          hasOrgChoice={hasChoice}
          onSuccess={handleSetupSuccess}
        />
      )}
    </div>
  );
};

export default IntegrationsPage;
